import { useAuth } from '../context/AuthContext';
import {
  UserRound, Mail, Building2, ShieldCheck, LogOut,
  CreditCard, ArrowLeftRight, ChevronRight, Star, Phone,
  MapPin, ArrowLeft, TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';

export default function UserProfile() {
  const { userData, logout } = useAuth();
  const navigate = useNavigate();

  const trustScore = userData?.personal?.nicNumber ? 820 : 420;
  const tier = trustScore >= 750 ? 'Gold' : trustScore >= 500 ? 'Silver' : 'Bronze';
  const tierColor = tier === 'Gold' ? 'text-amber-600' : tier === 'Silver' ? 'text-slate-500' : 'text-orange-700';
  const tierBg = tier === 'Gold'
    ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'
    : tier === 'Silver'
    ? 'bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200'
    : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200';

  const initials = (userData?.personal?.fullName || userData?.displayName || 'U')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('').toUpperCase();

  const scorePercent = (trustScore / 900) * 100;
  const scoreColor = trustScore >= 750 ? '#f59e0b' : trustScore >= 500 ? '#6b7280' : '#ea580c';

  const quickLinks = [
    { label: 'Identity Profile', desc: 'Manage personal information & KYC', icon: UserRound, path: '/profile' },
    { label: 'Bank Accounts', desc: 'Linked disbursement accounts', icon: CreditCard, path: '/banks' },
    { label: 'Transaction History', desc: 'Full activity & repayment log', icon: ArrowLeftRight, path: '/transactions' },
  ];

  const infoItems = [
    { icon: Mail, label: userData?.email || 'Not set' },
    { icon: Phone, label: userData?.personal?.mobileNumber || userData?.phoneNumber || 'Not set' },
    { icon: Building2, label: userData?.company || 'Independent' },
    { icon: MapPin, label: userData?.personal?.currentAddress ? (userData.personal.currentAddress.substring(0, 40) + (userData.personal.currentAddress.length > 40 ? '…' : '')) : 'Address not set' },
  ];

  return (
    <div className="min-h-screen bg-[#f6f9fc] pb-28">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-stripe-border sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-stripe-slate hover:text-stripe-dark transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm font-bold">Dashboard</span>
          </button>
          <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-7 w-auto object-contain" />
          <div className="w-20" /> {/* spacer */}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5">

        {/* Hero Profile Card */}
        <div className="bg-stripe-dark rounded-2xl overflow-hidden shadow-sm relative border border-stripe-dark/10">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-[0.04]"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full -mr-32 -mt-32 blur-3xl" />

          <div className="relative z-10 p-8">
            <div className="flex items-start gap-5 mb-8">
              {/* Avatar */}
              <div className="shrink-0 relative">
                {userData?.personal?.selfieUrl ? (
                  <img
                    src={userData.personal.selfieUrl}
                    alt="Profile"
                    className="w-20 h-20 rounded-xl object-cover border-2 border-white/20 shadow-lg"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-black text-white">{initials}</span>
                  </div>
                )}
                {/* Verified badge */}
                {userData?.personal?.nicNumber && (
                  <div className="absolute -bottom-2 -right-2 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center border-2 border-stripe-dark">
                    <ShieldCheck size={13} className="text-white" strokeWidth={3} />
                  </div>
                )}
              </div>              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-black text-white tracking-tighter leading-tight truncate">
                  {userData?.personal?.fullName || userData?.displayName || 'Unknown User'}
                </h1>
                <p className="text-white/50 text-sm font-medium mt-1">{userData?.company || 'Independent'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info Card */}
        <div className="bg-white rounded-2xl border border-stripe-border shadow-stripe overflow-hidden">
          <div className="px-6 py-4 border-b border-stripe-border">
            <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">Contact Information</p>
          </div>
          <div className="divide-y divide-stripe-border">
            {infoItems.map((item, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <div className="w-8 h-8 rounded-xl bg-stripe-bg border border-stripe-border flex items-center justify-center shrink-0">
                  <item.icon size={14} className="text-stripe-slate" />
                </div>
                <p className="text-sm font-semibold text-stripe-dark truncate">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-2xl border border-stripe-border shadow-stripe overflow-hidden">
          <div className="px-6 py-4 border-b border-stripe-border">
            <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">Quick Access</p>
          </div>
          {quickLinks.map((item, i) => (
            <button
              key={i}
              onClick={() => navigate(item.path)}
              className="w-full flex items-center justify-between px-6 py-5 hover:bg-stripe-bg transition-colors group text-left border-b border-stripe-border last:border-0"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-stripe-bg border border-stripe-border rounded-xl flex items-center justify-center text-stripe-slate group-hover:bg-stripe-dark group-hover:text-white group-hover:border-stripe-dark transition-all shrink-0">
                  <item.icon size={17} />
                </div>
                <div>
                  <p className="text-sm font-bold text-stripe-dark">{item.label}</p>
                  <p className="text-xs text-stripe-slate font-medium mt-0.5">{item.desc}</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-stripe-light group-hover:text-stripe-dark group-hover:translate-x-0.5 transition-all shrink-0" />
            </button>
          ))}
        </div>

        {/* Verification Status */}
        <div className={`rounded-2xl border p-6 flex items-center gap-4 ${
          userData?.personal?.nicNumber
            ? 'bg-green-50 border-green-200'
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
            userData?.personal?.nicNumber ? 'bg-green-100' : 'bg-amber-100'
          }`}>
            <ShieldCheck size={22} className={userData?.personal?.nicNumber ? 'text-green-600' : 'text-amber-600'} />
          </div>
          <div>
            <p className={`text-sm font-black ${userData?.personal?.nicNumber ? 'text-green-800' : 'text-amber-800'}`}>
              {userData?.personal?.nicNumber ? 'Identity Verified' : 'Verification Incomplete'}
            </p>
            <p className={`text-xs font-medium mt-0.5 ${userData?.personal?.nicNumber ? 'text-green-600' : 'text-amber-600'}`}>
              {userData?.personal?.nicNumber
                ? `NIC: ${userData.personal.nicNumber} · Full KYC Complete`
                : 'Complete your loan application to verify your identity'}
            </p>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={logout}
          className="w-full py-4 flex items-center justify-center gap-3 text-red-500 font-black bg-white border border-red-100 hover:bg-red-50 hover:border-red-200 rounded-2xl transition-all group text-sm"
        >
          <LogOut size={18} className="group-hover:-translate-x-1 transition-transform" />
          Sign Out
        </button>

        <p className="text-center text-[10px] font-bold text-stripe-slate/30 uppercase tracking-[0.2em] pb-2">
          Finova (Pvt) Ltd · Finance Easy Platform
        </p>
      </main>

      <BottomNav />
    </div>
  );
}
